import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { CreditorService } from './creditor.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CreditorCaseSummary } from '../interfaces/creditorCaseSummary.interface';
import { fadeInAnimation } from './../_animations/fade-in.animation';
import { ErrorModalService } from '../modal/error.modal.service';
import { CreditorInfo } from '../interfaces/creditorInfo.interface';
import { SessionService } from '../session/session.service';
import { TranslateService } from '@ngx-translate/core';
import { CreditorGroupInfo } from '../interfaces/creditorGroupInfo.interface';

@Component({
    selector: 'app-creditor-landing',
    templateUrl: './creditor-landing.component.html',
    styleUrls: ['./creditor-landing.component.scss'],
    animations: [
        fadeInAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreditorLandingComponent implements OnInit {
  public form: FormGroup;

  public get searchQueryField() { return this.form.get('searchQuery'); }
  public isSearching = false;
  public searched = false;
  public searchResults: CreditorCaseSummary[] | null = null;
  public myCreditorCases: CreditorInfo[] = [];
  public isLoadingMyCases = false;
  public readonly skeletonRows = [1, 2, 3];
  public group: CreditorGroupInfo | null = null;
  public isLoadingGroup = true;

  public get showResults(): boolean {
    return this.searched && !this.isSearching && this.searchResults !== null;
  }

  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  public get isInGroup(): boolean {
    return !!this.group?.groupId && this.group.groupId !== this.emptyGuid;
  }

  public get sortedMembers() {
    return (this.group?.members ?? []).slice().sort((a, b) => (b.isCurrentUser ? 1 : 0) - (a.isCurrentUser ? 1 : 0));
  }

  constructor(
    private creditorService: CreditorService,
    private formBuilder: FormBuilder,
    private errorModalService: ErrorModalService,
    private sessionService: SessionService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.sessionService.setSelectedCaseId(null);

    this.form = this.formBuilder.group({
      searchQuery: ['', [Validators.required, Validators.minLength(2)]]
    });
    this.loadMyCreditorCases();
    this.loadGroup();
  }

  private loadGroup(): void {
    this.isLoadingGroup = true;
    this.creditorService.getMyGroup().subscribe(
      group => {
        this.group = group;
        this.isLoadingGroup = false;
      },
      () => {
        this.isLoadingGroup = false;
      }
    );
  }

  private loadMyCreditorCases(): void {
    this.isLoadingMyCases = true;
    this.creditorService.getMyCreditorCases().subscribe(
      (myCreditorCases: CreditorInfo[]) => {
        this.myCreditorCases = myCreditorCases;
        this.isLoadingMyCases = false;
      },
      (error) => {
        this.isLoadingMyCases = false;
        this.errorModalService.open(this.translate.instant('error.load-error-title'), this.translate.instant('error.could-not-load-creditor-cases'));
      }
    );
  }

  public search(): void {
    if (this.isSearching) { return; }

    this.form.patchValue({ searchQuery: (this.searchQueryField?.value as string ?? '').trim() });

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSearching = true;
    this.searched = true;
    this.searchResults = null;

    const query = this.searchQueryField?.value as string;

    this.creditorService.searchCreditorCases(query).subscribe(
      (results: CreditorCaseSummary[]) => {
        this.isSearching = false;
        this.searchResults = results ?? [];
      },
      (error) => {
        this.isSearching = false;
        this.errorModalService.open(this.translate.instant('error.load-error-title'), this.translate.instant('error.could-not-search-cases'));
      }
    );
  }

  public navigateToResult(result: CreditorCaseSummary): void {
    this.router.navigate(['/borgenar/arende', result.caseId], { queryParams: { from: 'search', name: result.caseName } });
  }

  public trackByCaseId(index: number, item: CreditorCaseSummary): string {
    return item.caseId;
  }
}
